import React, { useCallback, useEffect } from 'react'
import { SafeAreaView } from 'react-native'
import { useFocusEffect } from '@react-navigation/native'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { GetClientByCode, MetricsLogin, PortalContatoPermissoes, SaveNotificationToken } from '@/domain/usecases'
import { KEY_ACCESS_PASSWORD, KEY_ACCESS_SELECTED_CONTACT, useAuth, useErrorHandler } from '@/presentation/hooks'
import { isLoadingState, Menu } from '@/presentation/pages/Home/components'
import { authenticationState, isOpenUpdatePasswordState, localStorageState, PhoneModal } from '@/presentation/components'
import { DateAdapter } from '@/data/protocols'
import { version } from '../../../../package.json'
import { PortalContatoModel } from '@/domain/models'
import { contatoClienteSelectedState } from '@/presentation/pages/Login/components'

type HomeProps = {
  getClientByCode: GetClientByCode
  navigation: any
  metricsLogin: MetricsLogin
  dateAdapter: DateAdapter
  getContatoPermissoes: PortalContatoPermissoes
}

export const Home: React.FC<HomeProps> = ({
  getClientByCode,
  navigation,
  metricsLogin,
  dateAdapter,
  getContatoPermissoes
}) => {
  const { setClient, getCurrentClient } = useAuth()
  const setLoading = useSetRecoilState(isLoadingState)
  const [autentication, setAuthentication] = useRecoilState(authenticationState)
  const setOpenUpdatePassword = useSetRecoilState(isOpenUpdatePasswordState)
  const localStorageAdapter = useRecoilValue(localStorageState)
  const handleError = useErrorHandler(console.error)
  const contatoClienteSelected = useRecoilValue(contatoClienteSelectedState)
  const currentClient = getCurrentClient()
  const portalContato = localStorageAdapter.get(KEY_ACCESS_SELECTED_CONTACT) as PortalContatoModel
  const codigoClienteFromContato = contatoClienteSelected?.CodCliente

  let codigoCliente = 0

  if (codigoClienteFromContato) {
    codigoCliente = Number(codigoClienteFromContato)
  } else if (currentClient) {
    if (!isNaN(Number(currentClient))) {
      codigoCliente = Number(currentClient)
    } else if (currentClient.includes('@')) {
      codigoCliente = Number(portalContato?.CodCliente ?? 0)
    }
  }

  const buscarPermissoes = async (codCliente: number): Promise<void> => {
    if (portalContato?.CodContato) {
      await getContatoPermissoes
        .get({
          codCliente,
          codContato: portalContato.CodContato
        })
        .then((res) => {
          setAuthentication(prev => {
            const client = { ...prev.client, permissoesContato: res, contato: portalContato }
            return { ...prev, client }
          })
        })
        .catch(handleError)
    }
  }

  useEffect(() => {
    setOpenUpdatePassword(!!localStorageAdapter.get(KEY_ACCESS_PASSWORD))

    return () => {
      setOpenUpdatePassword(false)
    }
  }, [])

  useFocusEffect(
    useCallback(() => {
      const tokenCliente = localStorageAdapter.get(SaveNotificationToken.RN.LOCAL_STORAGE_TOKEN)

      if (!codigoCliente) {
        setLoading(false)
        return
      }

      setLoading(true)
      getClientByCode
        .getByCode({
          codigoCliente,
          tokenDispositivo: tokenCliente
        })
        .then(async (client) => {
          setClient(client)
          await buscarPermissoes(codigoCliente)
          if (autentication.registerLogin) {
            void metricsLogin
              .register({
                dataOcorrencia: dateAdapter.format(dateAdapter.today(), 'YYYY-MM-DDTHH:mm:ssZ'),
                email: client.email,
                unidade: client.unidade,
                usuario: client.codCliente.toString(),
                versao: version
              })
              .then(() => setAuthentication(currentState => ({ ...currentState, registerLogin: false })))
              .catch(console.error)
          }
        })
        .catch(handleError)
        .finally(() => setLoading(false))
    }, [getCurrentClient, codigoCliente])
  )

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <Menu navigation={navigation} />
      <PhoneModal/>
    </SafeAreaView>
  )
}
